import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus, FolderOpen, Check, Users, Settings2, Upload, List, LayoutGrid, Columns3 } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { SelectorDeVista, type OpcionDeVista } from '@/components/ui/SelectorDeVista';
import { Spinner } from '@/components/ui/Spinner';
import { usePreferencia } from '@/hooks/usePreferencia';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { unaDe } from '@/lib/preferencias';
import * as frentesApi from '@/api/frentes.api';
import type { ChatEnLista, Frente } from '@/api/frentes.api';
import { hora12, fechaCorta, iniciales, nombreCorto } from '@/lib/athena';
import { SIN_CLASIFICAR, VISTAS_DE_FRENTES, chatsPorFrente, conPasos, movimientos, ultimaLinea, type PasoDeFrente, type VistaDeFrentes } from '@/lib/frentes';
import { Candado } from '@/components/athena/Candado';
import { abrirPestana } from '@/store/pestanas.store';
import { MosaicoDeFrentes, TableroDeFrentes } from '@/components/frentes/VistasDeFrentes';
import * as candado from '@/hooks/candado';

/**
 * CHATS — tus conversaciones, divididas como tú quieras.
 *
 * Esteban, 20-sep: *"no veo el chat en vivo dentro del programa... puedo
 * organizarlo por grupos de personas, dividir todo lo que soy de trabajo, todo
 * lo que son personales... la jerarquía se respete en todos los frentes que yo
 * abra. Ningún frente es más importante que otro."*
 *
 * ═══ LOS FRENTES SE DIBUJAN TODOS IGUAL ═══
 *
 * Mismo tamaño, misma fila, mismo peso tipográfico. El único que se ve distinto
 * es el que está abierto. Si "Trabajo" fuera más grande que "Familia", la
 * pantalla estaría contradiciendo lo que el dueño dijo de su propia vida.
 *
 * El color lo elige él; el programa no reparte importancia.
 *
 * ═══ "SIN CLASIFICAR" NO ES UN FRENTE ═══
 *
 * Es un filtro aparte, y existe para que nada quede invisible. Un chat sin
 * frente tiene que poder encontrarse, o el sistema de carpetas se convierte en
 * una forma de perder cosas.
 *
 * ═══ TRES VISTAS, LOS MISMOS CHATS (tanda 6) ═══
 *
 * Kervens: *"los frentes yo debería verlos también de tres maneras… dame tres
 * opciones y déjalas ahí y que el cliente escoja cómo verlo"*. La lista de
 * siempre, un mosaico (una tarjeta por frente) y un tablero (una columna por
 * frente, y los chats se arrastran de una a otra). Cómo se reparten vive en
 * `lib/frentes`, dibujarlos en `components/frentes`, y la elección sigue a la
 * persona de la laptop al teléfono (preferencia `frentes.vista`).
 */

const VISTAS: OpcionDeVista<VistaDeFrentes>[] = [
  { id: 'lista', etiqueta: 'Lista', icono: List, ayuda: 'Tus chats seguidos, con los frentes arriba para filtrar' },
  { id: 'mosaico', etiqueta: 'Mosaico', icono: LayoutGrid, ayuda: 'Una tarjeta por frente: cuántos chats, cuántos sin leer y los más recientes' },
  { id: 'tablero', etiqueta: 'Tablero', icono: Columns3, ayuda: 'Una columna por frente: arrastra un chat para cambiarlo de frente' },
];
const sanearVista = unaDe(VISTAS_DE_FRENTES, 'lista');

// ponytail: el mosaico y el tablero reparten los 200 chats más recientes, que es lo más que la API
// entrega en una llamada. Con más chats que eso, una ruta que agrupe en el servidor.
const TOPE = 200;
/** La caché de los chats sin filtro de frente (la del mosaico y el tablero), con cualquier búsqueda. */
const AGRUPADOS = ['chats', 'por-frente'] as const;
const PASAR = ['chats', 'pasar-de-frente'] as const;
type RespuestaDeChats = Awaited<ReturnType<typeof frentesApi.getChats>>;

function Burbuja({ f, activo, onClick }: { f: Frente; activo: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        // Todos del mismo tamaño: la jerarquía la pone el dueño, no el diseño.
        'flex min-h-[44px] shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm transition',
        activo
          ? 'border-transparent text-white'
          : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200',
      )}
      style={activo ? { backgroundColor: f.color } : undefined}
    >
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: activo ? '#fff' : f.color }}
      />
      {f.nombre}
      {typeof f.chats === 'number' && f.chats > 0 && (
        <span className={cn('text-xs', activo ? 'text-white/80' : 'text-slate-400')}>
          {f.chats}
        </span>
      )}
    </button>
  );
}

function FilaDeChat({
  c,
  frentes,
  onAbrir,
  onMover,
}: {
  c: ChatEnLista;
  frentes: Frente[];
  onAbrir: (id: string) => void;
  onMover: (chatId: string, frenteId: string, poner: boolean) => void;
}) {
  const [menu, setMenu] = useState(false);
  const enEste = new Set(c.frentes.map((f) => f.id));

  return (
    <div className="border-b border-slate-100 last:border-0 dark:border-slate-800">
      <div className="flex min-h-[64px] items-center gap-3 py-2.5">
        <button
          onClick={() => onAbrir(c.id)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-100 text-sm font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {c.esGrupo ? <Users className="h-4 w-4" /> : iniciales(c.nombre)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {nombreCorto(c.nombre)}
              </span>
              {c.lastMessageAt && (
                <span className="shrink-0 text-[11px] text-slate-400">
                  {new Date(c.lastMessageAt).toDateString() === new Date().toDateString()
                    ? hora12(c.lastMessageAt)
                    : fechaCorta(c.lastMessageAt)}
                </span>
              )}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="min-w-0 flex-1 truncate text-xs text-slate-500 dark:text-slate-400">
                {ultimaLinea(c)}
              </span>
              {c.frentes.map((f) => (
                <span
                  key={f.id}
                  title={f.nombre}
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: f.color }}
                />
              ))}
            </span>
          </span>
        </button>

        <button
          onClick={() => setMenu((v) => !v)}
          aria-label="Mover de frente"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <FolderOpen className="h-4 w-4" />
        </button>
      </div>

      {/* Un chat puede estar en varios frentes: son casillas, no una lista de
          opción única. El primo que además es cliente es de los dos. */}
      {menu && (
        <div className="flex flex-wrap gap-1.5 pb-3">
          {frentes.map((f) => {
            const puesto = enEste.has(f.id);
            return (
              <button
                key={f.id}
                onClick={() => onMover(c.id, f.id, !puesto)}
                className={cn(
                  'flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-xs transition',
                  puesto
                    ? 'border-transparent text-white'
                    : 'border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300',
                )}
                style={puesto ? { backgroundColor: f.color } : undefined}
              >
                {puesto && <Check className="h-3 w-3" />}
                {f.nombre}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Chats() {
  const [params, setParams] = useSearchParams();
  const [buscar, setBuscar] = useState('');
  const navegar = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const frenteId = params.get('frente') ?? undefined;
  const sinFrente = params.get('sin') === '1';
  // El candado se comprueba en cada render: `estaAbierto` mira la hora, asi
  // que a los dos minutos vuelve a pedir la llave sin necesidad de un reloj.
  const [desbloqueado, setDesbloqueado] = useState(candado.estaAbierto());

  const [vistaGuardada, setVista] = usePreferencia<VistaDeFrentes>('frentes.vista', 'lista', sanearVista);
  // Entrar a un frente desde el mosaico (o llegar con uno ya elegido en el
  // enlace, como hace el buscador) es mirar SU lista un rato, no cambiar de
  // gusto: la vista guardada sigue siendo la que la persona escogió.
  const [deVisita, setDeVisita] = useState(() => Boolean(frenteId) || sinFrente);
  const vista: VistaDeFrentes = deVisita ? 'lista' : vistaGuardada;
  const agrupada = vista !== 'lista';
  // Qué frente con candado se quiso abrir desde el tablero, sin salir de él.
  const [porAbrir, setPorAbrir] = useState<string | null>(null);

  const frentes = useQuery({ queryKey: ['frentes'], queryFn: frentesApi.getFrentes });
  const chats = useQuery({
    // El mosaico y el tablero reparten TODOS los chats: no llevan filtro de frente.
    queryKey: agrupada ? [...AGRUPADOS, buscar] : ['chats', frenteId, sinFrente, buscar],
    queryFn: () =>
      frentesApi.getChats(
        agrupada
          ? { buscar: buscar.trim() || undefined, limite: TOPE }
          : {
              frenteId,
              sinFrente: sinFrente || undefined,
              buscar: buscar.trim() || undefined,
              limite: 150,
            },
      ),
    // En el mosaico y el tablero, escribir en el buscador no vacía la pantalla
    // mientras llega la respuesta: se queda lo de la búsqueda anterior.
    placeholderData: (antes, consulta) =>
      agrupada && consulta?.queryKey[1] === AGRUPADOS[1] ? antes : undefined,
  });

  const mover = useMutation({
    mutationFn: ({ chatId, frenteId: f, poner }: { chatId: string; frenteId: string; poner: boolean }) =>
      frentesApi.moverChatDeFrente(chatId, f, poner),
    onSuccess: (_d, v) => {
      toast.success(v.poner ? 'Movido al frente' : 'Sacado del frente');
      void qc.invalidateQueries({ queryKey: ['chats'] });
      void qc.invalidateQueries({ queryKey: ['frentes'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Un chat se abre como pestaña arriba (T12), con su nombre; «Frentes» se queda debajo como estaba.
  const abrirChat = (id: string) => abrirPestana({ tipo: 'chat', id, titulo: chats.data?.chats.find((c) => c.id === id)?.nombre || 'Chat…' });

  const lista = frentes.data?.frentes ?? [];
  const elFrente = lista.find((f) => f.id === frenteId);

  /** Cambia UN chat en la caché del mosaico y del tablero. */
  const ponerEnCache = (chatId: string, cambio: (c: ChatEnLista) => ChatEnLista) =>
    qc.setQueriesData<RespuestaDeChats>(
      { queryKey: AGRUPADOS },
      (r) => r && { ...r, chats: r.chats.map((c) => (c.id === chatId ? cambio(c) : c)) },
    );

  // Pasar un chat de un frente a otro (soltarlo en otra columna, o su «Mover
  // a…»). La tarjeta cambia de columna en el acto; si el servidor no quiere,
  // vuelve a la suya y se dice por qué.
  const pasar = useMutation({
    mutationKey: PASAR,
    mutationFn: async ({ chat, pasos }: { chat: ChatEnLista; pasos: PasoDeFrente[] }) => {
      // De uno en uno y en su orden: primero entra al destino, después sale del origen.
      const hechos: PasoDeFrente[] = [];
      try {
        for (const p of pasos) {
          await frentesApi.moverChatDeFrente(chat.id, p.frenteId, p.poner);
          hechos.push(p);
        }
      } catch (e) {
        // Se quedó a medias (entró al destino y no salió del origen): se deshace lo que alcanzó a
        // hacerse, para que el servidor quede como la pantalla dice que quedó. Si deshacer también
        // falla, la lista se vuelve a pedir al terminar y enseña lo que haya (revisión de Codex).
        for (const p of hechos.reverse()) await frentesApi.moverChatDeFrente(chat.id, p.frenteId, !p.poner).catch(() => undefined);
        throw e;
      }
    },
    onMutate: async ({ chat, pasos }) => {
      await qc.cancelQueries({ queryKey: ['chats'] });
      ponerEnCache(chat.id, (c) => conPasos(c, pasos, lista));
    },
    onError: (e: Error, { chat }) => {
      // Solo ESTE chat vuelve a como estaba: devolver la lista entera pisaría otro movimiento reciente.
      ponerEnCache(chat.id, (c) => ({ ...c, frentes: chat.frentes }));
      toast.error('No se pudo mover el chat', e.message);
    },
    onSuccess: (_d, { pasos }) => {
      const nombreDe = (p?: PasoDeFrente) => lista.find((f) => f.id === p?.frenteId)?.nombre ?? 'el frente';
      const entra = pasos.find((p) => p.poner);
      toast.success(
        entra ? `Movido a «${nombreDe(entra)}»` : `Sacado de «${nombreDe(pasos.find((p) => !p.poner))}»`,
      );
    },
    onSettled: () => {
      // Con varios movimientos seguidos se vuelve a pedir la lista una vez, al terminar el último:
      // pedirla antes pisaría con datos viejos lo que todavía está en camino.
      if (qc.isMutating({ mutationKey: PASAR }) > 1) return;
      void qc.invalidateQueries({ queryKey: ['chats'] });
      void qc.invalidateQueries({ queryKey: ['frentes'] });
    },
  });

  const elegir = (id?: string, sin = false) => {
    const p = new URLSearchParams();
    if (sin) p.set('sin', '1');
    else if (id && id !== frenteId) p.set('frente', id);
    setParams(p, { replace: true });
  };

  const cambiarVista = (v: VistaDeFrentes) => {
    setDeVisita(false);
    setVista(v);
    // El mosaico y el tablero no llevan frente elegido: si se quedara en el
    // enlace, al recargar se abriría la lista de ese frente y no la vista escogida.
    if (v !== 'lista') setParams(new URLSearchParams(), { replace: true });
  };

  // Si el frente tiene llave y el candado esta cerrado, no se dibuja NADA de
  // su contenido: se dibuja el candado. Esconder las filas con CSS dejaria los
  // nombres en el HTML. Lo pide el frente elegido en la lista, o el que se
  // quiso abrir desde el tablero.
  const conLlave = desbloqueado
    ? undefined
    : agrupada
      ? lista.find((f) => f.id === porAbrir && f.conCandado)
      : elFrente?.conCandado
        ? elFrente
        : undefined;

  if (conLlave) {
    return (
      <Candado
        nombreDelFrente={conLlave.nombre}
        onAbierto={() => {
          setDesbloqueado(true);
          setPorAbrir(null);
        }}
        onCancelar={() => {
          // Se vuelve a donde se estaba: al tablero, o a la vista de siempre sin frente elegido.
          setPorAbrir(null);
          setDeVisita(false);
          if (!agrupada) elegir(undefined);
        }}
      />
    );
  }

  const grupos = agrupada ? chatsPorFrente(lista, chats.data?.chats ?? []) : [];
  const buscando = buscar.trim();

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Frentes</h1>
          <div className="flex shrink-0 items-center gap-1">
            <SelectorDeVista opciones={VISTAS} valor={vista} onCambiar={cambiarVista} etiqueta="Cómo ver tus frentes" />
            {/* Traer un historial va ACA y no en el menu: se busca cuando se
                esta mirando los chats y se ve que falta uno. */}
            <button
              onClick={() => navegar('/importar')}
              className="grid h-11 w-11 place-items-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Traer un historial"
              title="Traer un historial de WhatsApp"
            >
              <Upload className="h-5 w-5" />
            </button>
            <button
              onClick={() => navegar('/frentes')}
              className="grid h-11 w-11 place-items-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Administrar frentes"
            >
              <Settings2 className="h-5 w-5" />
            </button>
          </div>
        </div>
        {/* Debajo y no al lado del título: en el teléfono, junto al selector se partía en tres renglones. */}
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Tus conversaciones, divididas como tú quieras.
        </p>
      </div>

      {/* LOS FRENTES. Todos del mismo tamaño, en una sola fila que rueda.
          Son el filtro de la lista: en el mosaico y el tablero los frentes ya
          son las tarjetas y las columnas. */}
      {!agrupada && (
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 motion-safe:animate-fade-in sm:mx-0 sm:flex-wrap sm:px-0">
          <button
            onClick={() => elegir(undefined)}
            className={cn(
              'min-h-[44px] shrink-0 rounded-full border px-3.5 text-sm transition',
              !frenteId && !sinFrente
                ? 'border-transparent bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                : 'border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200',
            )}
          >
            Todos
          </button>

          {lista.map((f) => (
            <Burbuja key={f.id} f={f} activo={f.id === frenteId} onClick={() => elegir(f.id)} />
          ))}

          <button
            onClick={() => elegir(undefined, true)}
            className={cn(
              'min-h-[44px] shrink-0 rounded-full border border-dashed px-3.5 text-sm transition',
              sinFrente
                ? 'border-transparent bg-slate-600 text-white'
                : 'border-slate-300 text-slate-500 dark:border-slate-600 dark:text-slate-400',
            )}
          >
            Sin clasificar
          </button>

          <button
            onClick={() => navegar('/frentes')}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-dashed border-slate-300 text-slate-400 dark:border-slate-600"
            aria-label="Nuevo frente"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          placeholder="Buscar por nombre o teléfono…"
          className="min-h-[44px] w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm dark:border-slate-700 dark:bg-slate-900"
        />
      </div>

      {agrupada ? (
        chats.isPending || frentes.isPending ? (
          <Card>
            <CardBody>
              <Spinner />
            </CardBody>
          </Card>
        ) : chats.isError || frentes.isError ? (
          <Card>
            <CardBody>
              <p className="py-6 text-center text-sm text-red-600 dark:text-red-300">
                No se pudieron leer tus chats.
              </p>
            </CardBody>
          </Card>
        ) : (
          // Cambiar de vista entra con un fundido.
          <div key={vista} className="space-y-3 motion-safe:animate-fade-in">
            {(chats.data?.chats.length ?? 0) === 0 && (
              <p className="rounded-lg bg-slate-100 p-2.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {buscando
                  ? `Nada con "${buscando}".`
                  : 'Todavía no hay conversaciones. Aparecen cuando conectes tu WhatsApp.'}
              </p>
            )}
            {vista === 'tablero' ? (
              <TableroDeFrentes
                grupos={grupos}
                candadoAbierto={desbloqueado}
                ahora={new Date()}
                buscando={buscando}
                onAbrir={abrirChat}
                onMover={(chat, de, a) => {
                  const pasos = movimientos(chat, de, a);
                  if (pasos.length > 0) pasar.mutate({ chat, pasos });
                }}
                onAbrirCandado={setPorAbrir}
              />
            ) : (
              <MosaicoDeFrentes
                grupos={grupos}
                candadoAbierto={desbloqueado}
                ahora={new Date()}
                buscando={buscando}
                onElegir={(id) => {
                  // Abre ese frente en la lista (si tiene candado, la lista pide la llave).
                  setDeVisita(true);
                  setParams(id === SIN_CLASIFICAR ? { sin: '1' } : { frente: id }, { replace: true });
                }}
                onAdministrar={() => navegar('/frentes')}
              />
            )}
            {(chats.data?.chats.length ?? 0) >= TOPE && (
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Aquí salen tus {TOPE} chats más recientes. Si buscas uno más viejo, escribe su nombre arriba.
              </p>
            )}
          </div>
        )
      ) : (
        <Card className="motion-safe:animate-fade-in">
          <CardBody className="space-y-0">
            {chats.isPending || frentes.isPending ? (
              <Spinner />
            ) : chats.isError ? (
              <p className="py-6 text-center text-sm text-red-600 dark:text-red-300">
                No se pudieron leer tus chats.
              </p>
            ) : (chats.data?.chats.length ?? 0) === 0 ? (
              <p className="py-10 text-center text-sm text-slate-400">
                {buscar
                  ? `Nada con "${buscar}".`
                  : sinFrente
                    ? 'Todos tus chats están clasificados.'
                    : frenteId
                      ? 'Este frente todavía no tiene chats. Muévelos con el botón de la carpeta.'
                      : 'Todavía no hay conversaciones. Aparecen cuando conectes tu WhatsApp.'}
              </p>
            ) : (
              chats.data?.chats.map((c) => (
                <FilaDeChat
                  key={c.id}
                  c={c}
                  frentes={lista}
                  onAbrir={abrirChat}
                  onMover={(chatId, f, poner) => mover.mutate({ chatId, frenteId: f, poner })}
                />
              ))
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
